import { useEffect } from "react";

// Keeps the user's place on a screen when the page briefly gets shorter.
//
// After a save, most screens reload their list and show a spinner (or fewer rows) for a moment.
// The page shrinks, the browser has to pull the scroll position up, and when the rows come back
// the user is left at the top and must scroll down again. This remembers where the user was and,
// once the content is tall enough again, puts them back there.
//
// Only a forced jump is undone: scrolling by the user (wheel, touch, keys, scrollbar) is kept,
// and moving to another screen or changing its filters/search starts fresh.
const RESTORE_WINDOW_MS = 15000;   // give slow reloads time, but never yank the page much later

export default function ScrollKeeper() {
  useEffect(() => {
    const maxScroll = () => document.documentElement.scrollHeight - window.innerHeight;
    const pageKey = () => window.location.pathname + window.location.search;

    let key = pageKey();
    let wanted = window.scrollY;   // where the user wants to be
    let clampedAt = 0;             // when the browser last forced the page up (0 = not waiting)

    const resetIfNewPage = () => {
      if (pageKey() !== key) { key = pageKey(); wanted = window.scrollY; clampedAt = 0; return true; }
      return false;
    };

    const onScroll = () => {
      if (resetIfNewPage()) return;
      const y = window.scrollY;
      if (y < wanted - 1 && maxScroll() < wanted - 1) {
        // The page got too short to stay where we were: remember to come back.
        if (!clampedAt) clampedAt = Date.now();
        return;
      }
      if (!clampedAt) wanted = y;
    };

    const restore = () => {
      if (resetIfNewPage() || !clampedAt) return;
      if (Date.now() - clampedAt > RESTORE_WINDOW_MS) { clampedAt = 0; wanted = window.scrollY; return; }
      if (maxScroll() >= wanted - 1) {
        clampedAt = 0;
        if (Math.abs(window.scrollY - wanted) > 1) window.scrollTo(0, wanted);
      }
    };

    // The user took over (scrolled, dragged the scrollbar, pressed a key): respect where they go.
    const userMoved = () => { clampedAt = 0; wanted = window.scrollY; };
    const onPointerDown = (e) => { if (e.clientX >= document.documentElement.clientWidth) userMoved(); };
    const NAV_KEYS = new Set(["PageUp", "PageDown", "Home", "End", "ArrowUp", "ArrowDown", " "]);
    const onKeyDown = (e) => {
      const t = e.target;
      const typing = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      if (!typing && NAV_KEYS.has(e.key)) userMoved();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("wheel", userMoved, { passive: true });
    window.addEventListener("touchmove", userMoved, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown);

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(restore) : null;
    if (ro) ro.observe(document.body);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wheel", userMoved);
      window.removeEventListener("touchmove", userMoved);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown);
      if (ro) ro.disconnect();
    };
  }, []);
  return null;
}
